// 参照専用の埋め込みモード用の認証ハンドオフ。
//
// クロスサイト iframe（Connect Note の記事ページ配下）では、RC の通常認証（OIDC リダイレクト＋
// IndexedDB/localStorage）が使えない（サードパーティ文脈でストレージが分離され、IdP も iframe を
// 拒否する）。そこで親フレーム（Connect Note）が対象 canvasId に束縛された短命の埋め込みチケットを
// postMessage で渡し、RC 埋め込みはそれを API の Bearer に使う。サービス全体スコープの canvas-read
// アクセストークンはブラウザへ渡さない。チケットはメモリ内のみに保持し、ストレージへは
// 一切書かない（分離ストレージ安全・トークン残留防止）。
//
// プロトコル:
//   iframe → 親: { type: "embed-auth-ready" }      … リスナー設置完了を通知（取りこぼし防止のハンドシェイク）
//   親 → iframe: { type: "embed-auth", token, expiresAt? } … 埋め込みチケット受け渡し（token フィールドにチケット）
//   iframe → 親: { type: "embed-auth-refresh" }     … 失効前後の再発行要求
//
// セキュリティ: 受理は event.origin が getWebConfig().embedParentOrigins に含まれる場合のみ。
// 送信の targetOrigin は必ず具体オリジン（"*" は使わない）。チケット値はログへ出さない。
import { getWebConfig } from "../env";

const PARENT_MESSAGE_TYPE_AUTH = "embed-auth";
const CHILD_MESSAGE_TYPE_READY = "embed-auth-ready";
const CHILD_MESSAGE_TYPE_REFRESH = "embed-auth-refresh";

/** 期限のこの余裕（ms）を切ったチケットは失効扱いにする。 */
const TOKEN_EXPIRY_GRACE_MS = 30_000;
/** 期限のこの余裕（ms）前に先行して再発行を要求する。 */
const REFRESH_MARGIN_MS = 60_000;

interface EmbedToken {
  readonly token: string;
  /** 親フレームから受け取った expiresAt 由来のエポック ms。取得できなければ undefined（先行 refresh は行わない）。 */
  readonly expiresAtMs: number | undefined;
}

let current: EmbedToken | undefined;
/** 最初に受理した有効メッセージの origin をロックし、以降の送信先に使う。 */
let parentOrigin: string | undefined;
let refreshTimer: number | undefined;
let listenerInstalled = false;
const subscribers = new Set<() => void>();

function allowedParentOrigins(): readonly string[] {
  try {
    return getWebConfig().embedParentOrigins;
  } catch {
    return [];
  }
}

function notifySubscribers(): void {
  for (const callback of subscribers) {
    callback();
  }
}

/** 親フレームへメッセージを送る。iframe でない（親＝自分）ときは何もしない。 */
function postToParent(message: { readonly type: string }): void {
  if (typeof window === "undefined" || window.parent === window) {
    return;
  }
  if (parentOrigin !== undefined) {
    window.parent.postMessage(message, parentOrigin);
    return;
  }
  // 親オリジン未確定の間は、許可済みオリジンそれぞれへ送る（targetOrigin が一致する親にだけ届く）。
  for (const origin of allowedParentOrigins()) {
    window.parent.postMessage(message, origin);
  }
}

function scheduleProactiveRefresh(): void {
  if (typeof window === "undefined") {
    return;
  }
  if (refreshTimer !== undefined) {
    window.clearTimeout(refreshTimer);
    refreshTimer = undefined;
  }
  const expiresAtMs = current?.expiresAtMs;
  if (expiresAtMs === undefined) {
    return;
  }
  const delay = Math.max(0, Math.min(expiresAtMs - REFRESH_MARGIN_MS - Date.now(), 2_147_483_647));
  refreshTimer = window.setTimeout(() => {
    requestEmbedTokenRefresh();
  }, delay);
}

function handleMessage(event: MessageEvent): void {
  if (!allowedParentOrigins().includes(event.origin)) {
    return;
  }
  const data: unknown = event.data;
  if (typeof data !== "object" || data === null) {
    return;
  }
  const record = data as Record<string, unknown>;
  if (record["type"] !== PARENT_MESSAGE_TYPE_AUTH) {
    return;
  }
  const token = record["token"];
  if (typeof token !== "string" || token.length === 0) {
    return;
  }
  const expiresAtRaw = record["expiresAt"];
  const parsedExpiresAt = typeof expiresAtRaw === "string" ? Date.parse(expiresAtRaw) : Number.NaN;
  // 受理した origin を親としてロックする（以降の refresh 送信先を固定）。
  parentOrigin = event.origin;
  current = {
    token,
    expiresAtMs: Number.isFinite(parsedExpiresAt) ? parsedExpiresAt : undefined,
  };
  scheduleProactiveRefresh();
  notifySubscribers();
}

/**
 * 親フレームからのチケット受け渡しリスナーを設置し、`embed-auth-ready` を通知する。
 * 返り値の関数でリスナーとタイマーを解除する。
 */
export function startEmbedAuthListener(): () => void {
  if (typeof window === "undefined") {
    return () => {
      // SSR 等では何もしない。
    };
  }
  if (!listenerInstalled) {
    window.addEventListener("message", handleMessage);
    listenerInstalled = true;
  }
  // リスナー設置後に ready を通知する（親はこれを待ってチケットを送る＝取りこぼし防止）。
  postToParent({ type: CHILD_MESSAGE_TYPE_READY });
  return () => {
    if (typeof window === "undefined") {
      return;
    }
    window.removeEventListener("message", handleMessage);
    listenerInstalled = false;
    if (refreshTimer !== undefined) {
      window.clearTimeout(refreshTimer);
      refreshTimer = undefined;
    }
  };
}

/** 現在有効な埋め込みチケットを返す。未受領・失効間近は undefined。 */
export function getEmbedAccessToken(): string | undefined {
  if (current === undefined) {
    return undefined;
  }
  if (
    current.expiresAtMs !== undefined &&
    current.expiresAtMs - TOKEN_EXPIRY_GRACE_MS <= Date.now()
  ) {
    return undefined;
  }
  return current.token;
}

/** 親フレームへチケット再発行を要求する。 */
export function requestEmbedTokenRefresh(): void {
  postToParent({ type: CHILD_MESSAGE_TYPE_REFRESH });
}

/** チケット受領を購読する。返り値で解除する。 */
export function subscribeEmbedToken(callback: () => void): () => void {
  subscribers.add(callback);
  return () => {
    subscribers.delete(callback);
  };
}

/** 有効チケットを待つ（既にあれば即解決）。timeout 経過時は現在値（undefined の可能性あり）で解決。 */
export function waitForEmbedToken(timeoutMs: number): Promise<string | undefined> {
  const existing = getEmbedAccessToken();
  if (existing !== undefined) {
    return Promise.resolve(existing);
  }
  if (typeof window === "undefined") {
    return Promise.resolve(undefined);
  }
  return new Promise<string | undefined>((resolve) => {
    let settled = false;
    const finish = (value: string | undefined): void => {
      if (settled) {
        return;
      }
      settled = true;
      unsubscribe();
      window.clearTimeout(timer);
      resolve(value);
    };
    const unsubscribe = subscribeEmbedToken(() => {
      const token = getEmbedAccessToken();
      if (token !== undefined) {
        finish(token);
      }
    });
    const timer = window.setTimeout(() => finish(getEmbedAccessToken()), timeoutMs);
  });
}

/**
 * 直前のチケットと「異なる」有効チケットを待つ。401 retry（forceRefresh）で、サーバに拒否された
 * 現行チケットを待機関数が即返して stale なまま再送してしまうのを防ぐ。既に current が previousToken と
 * 異なる有効チケットならそれを返し、timeout 時は undefined を返す（現行チケットにはフォールバックしない）。
 */
export function waitForNextEmbedToken(
  previousToken: string | undefined,
  timeoutMs: number,
): Promise<string | undefined> {
  const now = getEmbedAccessToken();
  if (now !== undefined && now !== previousToken) {
    return Promise.resolve(now);
  }
  if (typeof window === "undefined") {
    return Promise.resolve(undefined);
  }
  return new Promise<string | undefined>((resolve) => {
    let settled = false;
    const finish = (value: string | undefined): void => {
      if (settled) {
        return;
      }
      settled = true;
      unsubscribe();
      window.clearTimeout(timer);
      resolve(value);
    };
    const unsubscribe = subscribeEmbedToken(() => {
      const token = getEmbedAccessToken();
      if (token !== undefined && token !== previousToken) {
        finish(token);
      }
    });
    // timeout 時は現行（拒否された可能性のある）チケットではなく undefined を返し、retry を打ち切らせる。
    const timer = window.setTimeout(() => finish(undefined), timeoutMs);
  });
}

/** テスト用: モジュール内状態を初期化する。 */
export function resetEmbedAuthForTest(): void {
  current = undefined;
  parentOrigin = undefined;
  subscribers.clear();
  if (typeof window !== "undefined") {
    if (refreshTimer !== undefined) {
      window.clearTimeout(refreshTimer);
      refreshTimer = undefined;
    }
    if (listenerInstalled) {
      window.removeEventListener("message", handleMessage);
      listenerInstalled = false;
    }
  }
}
